import {
  FETCH_BOOKINGS,
  FETCH_BOOKINGS_SUCCESS,
  FETCH_BOOKINGS_FAILED,
  UPDATE_BOOKING,
  CANCEL_BOOKING
} from "../store/types";
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';
import { getSupabase } from '../config/configureSupabase';
import { uploadMedia } from '../other/supabaseStorage';

let supabaseRoutesChannel = null;

const mapRouteToBooking = (r) => {
  const origin = typeof r.origin === 'object' ? r.origin : { add: r.origin || '' };
  const dest = typeof r.destination === 'object' ? r.destination : { add: r.destination || '' };
  return {
    ...r,
    id: r.id,
    booking_id: r.id,
    reference: r.reference_code || r.id?.substring(0, 8),
    pickup: origin,
    pickupAddress: origin?.add || origin?.address || '',
    drop: dest,
    dropAddress: dest?.add || dest?.address || '',
    driver: r.driver_id,
    driver_id: r.driver_id,
    vehicle_id: r.vehicle_id,
    status: r.status,
    discount: 0,
    cashPaymentAmount: 0,
    cardPaymentAmount: 0,
    trip_cost: r.total_distance_km ? (Number(r.total_distance_km) * 2.5).toFixed(2) : 0,
    estimate: r.total_distance_km ? (Number(r.total_distance_km) * 2.5).toFixed(2) : 0,
    estimateDistance: r.total_distance_km || 0,
    estimateTime: r.total_duration_min ? r.total_duration_min * 60 : 0
  };
};

export const fetchBookings = () => (dispatch) => {
  const supabase = getSupabase();
  const userInfo = store.getState().auth?.profile;

  if (!supabase || !userInfo) {
    dispatch({
      type: FETCH_BOOKINGS_FAILED,
      payload: store.getState().languagedata?.defaultLanguage?.no_bookings || 'No bookings'
    });
    return;
  }

  dispatch({
    type: FETCH_BOOKINGS,
    payload: null,
  });

  const loadSupabaseRoutes = async () => {
    try {
      let query = supabase.from('routes').select('*').order('created_at', { ascending: false });
      if (userInfo.tenant_id) {
        query = query.eq('tenant_id', userInfo.tenant_id);
      }
      if (userInfo.usertype === 'driver') {
        query = query.or(`driver_id.eq.${userInfo.uid},status.in.(PENDING,NEW)`);
      } else if (userInfo.usertype === 'customer') {
        query = query.eq('customer_id', userInfo.uid);
      }

      const { data, error } = await query;
      if (!error && data) {
        const bookings = data.map(mapRouteToBooking);
        const active = [];
        let tracked = null;

        for (let i = 0; i < bookings.length; i++) {
          if (['PENDING', 'ASSIGNED', 'NEW', 'ACCEPTED', 'ARRIVED', 'STARTED', 'REACHED', 'PAYMENT_PENDING', 'PAID'].indexOf(bookings[i].status) !== -1) {
            active.push(bookings[i]);
          }
          if (['ASSIGNED', 'ACCEPTED', 'ARRIVED', 'STARTED'].indexOf(bookings[i].status) !== -1 && userInfo.usertype === 'driver') {
            tracked = bookings[i];
          }
        }

        dispatch({
          type: FETCH_BOOKINGS_SUCCESS,
          payload: {
            bookings: bookings,
            active: active,
            tracked: tracked
          },
        });
        return;
      }
    } catch (err) {
      console.warn('fetchBookings supabase err:', err);
      dispatch({
        type: FETCH_BOOKINGS_FAILED,
        payload: store.getState().languagedata?.defaultLanguage?.no_bookings || 'No bookings'
      });
    }
  };

  loadSupabaseRoutes();

  if (!supabaseRoutesChannel) {
    supabaseRoutesChannel = supabase
      .channel('public:routes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'routes' }, () => {
        loadSupabaseRoutes();
      })
      .subscribe();
  }
};

export const updateBooking = (booking) => async (dispatch) => {
  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });

  const supabase = getSupabase();
  if (supabase && booking?.id) {
    try {
      const updatePayload = { updated_at: new Date().toISOString() };
      if (booking.status) updatePayload.status = booking.status;
      if (booking.driver || booking.driver_id) updatePayload.driver_id = booking.driver || booking.driver_id;
      if (booking.vehicle_id) updatePayload.vehicle_id = booking.vehicle_id;
      if (booking.status === 'STARTED') updatePayload.started_at = new Date().toISOString();
      if (booking.status === 'COMPLETE' || booking.status === 'REACHED') updatePayload.completed_at = new Date().toISOString();
      if (booking.notes) updatePayload.notes = booking.notes;

      await supabase.from('routes').update(updatePayload).eq('id', booking.id);

      if (booking.customer_token && booking.status === 'ARRIVED') {
        RequestPushMsg(booking.customer_token, {
          title: store.getState().languagedata?.defaultLanguage?.notification_title || 'Alerta',
          msg: store.getState().languagedata?.defaultLanguage?.driver_near || 'Motorista chegou',
          screen: 'BookedCab',
          params: { bookingId: booking.id }
        });
      }
    } catch (e) {
      console.warn('updateBooking supabase error:', e);
    }
  }
};

export const cancelBooking = (data) => async (dispatch) => {
  dispatch({
    type: CANCEL_BOOKING,
    payload: data,
  });

  const supabase = getSupabase();
  if (supabase && data?.booking?.id) {
    try {
      await supabase.from('routes').update({
        status: 'CANCELLED',
        notes: data.reason || 'Cancelled',
        updated_at: new Date().toISOString()
      }).eq('id', data.booking.id);

      if (data.booking.driver_token) {
        RequestPushMsg(data.booking.driver_token, {
          title: store.getState().languagedata?.defaultLanguage?.notification_title || 'Alerta',
          msg: (store.getState().languagedata?.defaultLanguage?.booking_cancelled || 'Corrida cancelada: ') + data.booking.id,
          screen: 'BookedCab',
          params: { bookingId: data.booking.id }
        });
      }
    } catch (e) {
      console.warn('cancelBooking supabase error:', e);
    }
  }
};

export const updateBookingImage = (booking, imageType, imageBlob) => async (dispatch) => {
  if (!booking?.id || !imageBlob) return;
  const publicUrl = await uploadMedia('media', `bookings/${booking.id}/${imageType}.jpg`, imageBlob, 'image/jpeg');
  if (publicUrl) {
    const updated = { ...booking, [imageType]: publicUrl };
    dispatch({
      type: UPDATE_BOOKING,
      payload: updated,
    });
    const supabase = getSupabase();
    if (supabase) {
      await supabase.from('routes').update({
        [imageType]: publicUrl,
        updated_at: new Date().toISOString()
      }).eq('id', booking.id);
    }
  }
};

export const forceEndBooking = (booking) => async (dispatch) => {
  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });

  const supabase = getSupabase();
  if (supabase && booking?.id) {
    try {
      await supabase.from('routes').update({
        status: 'COMPLETE',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }).eq('id', booking.id);
    } catch (e) {
      console.warn('forceEndBooking supabase error:', e);
    }
  }
};
